const LONG_PRESS_DURATION_MS = 450;
const MOVE_TOLERANCE_PX = 12;
const SUPPRESS_AFTER_LONG_PRESS_MS = 2_500;
const SUPPRESS_AFTER_SELECTION_MS = 1_500;
const AUTHORIZED_FILE_TAP_MS = 750;

interface ActiveGesture {
  id: number;
  startedAt: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  target: EventTarget | null;
  moved: boolean;
}

interface FileGestureState {
  pointer: ActiveGesture | null;
  touch: ActiveGesture | null;
  selectionActive: boolean;
  suppressUntil: number;
  authorizedFileTapUntil: number;
}

const state: FileGestureState = {
  pointer: null,
  touch: null,
  selectionActive: false,
  suppressUntil: 0,
  authorizedFileTapUntil: 0,
};

export function installFileOpenGestureGuard(): () => void {
  resetState();

  function onPointerDown(event: PointerEvent) {
    if (!event.isPrimary || event.button !== 0) return;
    state.touch = null;
    state.pointer = createGesture(event.pointerId, event.clientX, event.clientY, event.target);
  }

  function onPointerMove(event: PointerEvent) {
    const gesture = state.pointer;
    if (!gesture || gesture.id !== event.pointerId) return;
    updateGesture(gesture, event.clientX, event.clientY);
  }

  function onPointerUp(event: PointerEvent) {
    const gesture = state.pointer;
    if (!gesture || gesture.id !== event.pointerId) return;
    updateGesture(gesture, event.clientX, event.clientY);
    completeGesture(gesture, event.target);
    state.pointer = null;
  }

  function onPointerCancel(event: PointerEvent) {
    if (!state.pointer || state.pointer.id !== event.pointerId) return;
    suppressFileOpen(SUPPRESS_AFTER_LONG_PRESS_MS);
    state.pointer = null;
  }

  function onTouchStart(event: TouchEvent) {
    if (state.pointer || event.touches.length !== 1) return;
    const touch = event.touches[0];
    if (!touch) return;
    state.touch = createGesture(touch.identifier, touch.clientX, touch.clientY, event.target);
  }

  function onTouchMove(event: TouchEvent) {
    const gesture = state.touch;
    if (!gesture) return;
    const touch = findTouch(event.touches, gesture.id);
    if (touch) updateGesture(gesture, touch.clientX, touch.clientY);
  }

  function onTouchEnd(event: TouchEvent) {
    const gesture = state.touch;
    if (!gesture) return;
    const touch = findTouch(event.changedTouches, gesture.id);
    if (touch) updateGesture(gesture, touch.clientX, touch.clientY);
    completeGesture(gesture, event.target);
    state.touch = null;
  }

  function onTouchCancel() {
    if (!state.touch) return;
    suppressFileOpen(SUPPRESS_AFTER_LONG_PRESS_MS);
    state.touch = null;
  }

  function onContextMenu() {
    suppressFileOpen(SUPPRESS_AFTER_LONG_PRESS_MS);
  }

  function onKeyDown(event: KeyboardEvent) {
    if ((event.key === "Enter" || event.key === " ") && fileIntentElement(event.target)) {
      state.authorizedFileTapUntil = performance.now() + AUTHORIZED_FILE_TAP_MS;
    }
  }

  function onSelectionChange() {
    const selection = document.getSelection();
    const active = Boolean(selection && !selection.isCollapsed);
    if (active) {
      state.selectionActive = true;
      suppressFileOpen(SUPPRESS_AFTER_LONG_PRESS_MS);
      return;
    }
    if (!state.selectionActive) return;
    state.selectionActive = false;
    suppressFileOpen(SUPPRESS_AFTER_SELECTION_MS);
  }

  function onVisibilityChange() {
    if (!document.hidden) return;
    if (state.pointer || state.touch) suppressFileOpen(SUPPRESS_AFTER_LONG_PRESS_MS);
    state.pointer = null;
    state.touch = null;
  }

  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("pointermove", onPointerMove, true);
  document.addEventListener("pointerup", onPointerUp, true);
  document.addEventListener("pointercancel", onPointerCancel, true);
  document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
  document.addEventListener("touchmove", onTouchMove, { capture: true, passive: true });
  document.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
  document.addEventListener("touchcancel", onTouchCancel, { capture: true, passive: true });
  document.addEventListener("contextmenu", onContextMenu, true);
  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("selectionchange", onSelectionChange, true);
  document.addEventListener("visibilitychange", onVisibilityChange, true);

  return () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("pointermove", onPointerMove, true);
    document.removeEventListener("pointerup", onPointerUp, true);
    document.removeEventListener("pointercancel", onPointerCancel, true);
    document.removeEventListener("touchstart", onTouchStart, true);
    document.removeEventListener("touchmove", onTouchMove, true);
    document.removeEventListener("touchend", onTouchEnd, true);
    document.removeEventListener("touchcancel", onTouchCancel, true);
    document.removeEventListener("contextmenu", onContextMenu, true);
    document.removeEventListener("keydown", onKeyDown, true);
    document.removeEventListener("selectionchange", onSelectionChange, true);
    document.removeEventListener("visibilitychange", onVisibilityChange, true);
    resetState();
  };
}

export function shouldSuppressFileOpen(): boolean {
  const now = performance.now();
  const authorized = now <= state.authorizedFileTapUntil;
  state.authorizedFileTapUntil = 0;
  if (authorized) return false;
  return state.selectionActive || now < state.suppressUntil;
}

function createGesture(id: number, x: number, y: number, target: EventTarget | null): ActiveGesture {
  return {
    id,
    startedAt: performance.now(),
    startX: x,
    startY: y,
    endX: x,
    endY: y,
    target,
    moved: false,
  };
}

function updateGesture(gesture: ActiveGesture, x: number, y: number) {
  gesture.endX = x;
  gesture.endY = y;
  if (
    Math.abs(x - gesture.startX) > MOVE_TOLERANCE_PX
    || Math.abs(y - gesture.startY) > MOVE_TOLERANCE_PX
  ) gesture.moved = true;
}

function completeGesture(gesture: ActiveGesture, endTarget: EventTarget | null) {
  const now = performance.now();
  const duration = now - gesture.startedAt;
  if (gesture.moved || duration >= LONG_PRESS_DURATION_MS || state.selectionActive) {
    suppressFileOpen(SUPPRESS_AFTER_LONG_PRESS_MS);
    return;
  }
  const intent = fileIntentElement(endTarget) ?? fileIntentElement(gesture.target);
  const hit = document.elementFromPoint(gesture.endX, gesture.endY);
  if (intent && hit && (hit === intent || intent.contains(hit))) {
    state.authorizedFileTapUntil = now + AUTHORIZED_FILE_TAP_MS;
  }
}

function fileIntentElement(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element
    ? target.closest<HTMLElement>("[data-file-open-control='true'], [data-file-open-intent='true']")
    : null;
}

function suppressFileOpen(duration: number) {
  state.authorizedFileTapUntil = 0;
  state.suppressUntil = Math.max(state.suppressUntil, performance.now() + duration);
}

function findTouch(list: TouchList, identifier: number): Touch | null {
  for (let index = 0; index < list.length; index += 1) {
    const touch = list.item(index);
    if (touch?.identifier === identifier) return touch;
  }
  return null;
}

function resetState() {
  state.pointer = null;
  state.touch = null;
  state.selectionActive = false;
  state.suppressUntil = 0;
  state.authorizedFileTapUntil = 0;
}
